import { cur } from '@/lib/currency';
import { useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Brain, TrendingDown, CheckCircle2, AlertTriangle, Loader2, Download, ChevronDown, ChevronRight, ArrowRightLeft, Users, ArrowUpDown } from 'lucide-react';
import { toLocalYmd } from '@/lib/dateRange';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// iter520 — Each branch (loss OR healthy) renders identical card with
// optional expense-details drilldown fetched on demand.
function BranchRow({ b, start, end, kind }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !details && b.branch_id) {
      setBusy(true);
      try {
        const r = await api.get('/ai-insights/branch-loss/expense-details', {
          params: { branch_id: b.branch_id, start_date: start, end_date: end },
        });
        setDetails(r.data);
      } catch (e) {
        toast.error('Failed to load expense details');
      } finally { setBusy(false); }
    }
  };

  const isLoss = kind === 'loss';
  const isWeak = kind === 'weak';
  const badgeCls = isLoss
    ? 'bg-error/20 text-error border-error/30'
    : isWeak
      ? 'bg-warning/20 text-warning border-warning/30'
      : 'bg-success/20 text-success border-success/30';
  const badgeText = isLoss ? 'LOSS' : isWeak ? 'WEAK MARGIN' : 'HEALTHY';

  return (
    <div className="p-4" data-testid={`branch-row-${b.branch_id || b.name}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggle}
            className="text-muted-foreground hover:text-foreground p-0.5 -ml-1"
            data-testid={`branch-toggle-${b.branch_id || b.name}`}
            title={open ? 'Hide expense details' : 'Show expense details'}
          >
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <div className="font-semibold font-outfit">{b.name}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`text-[10px] border ${badgeCls}`} data-testid={`branch-badge-${b.branch_id || b.name}`}>
            {badgeText}
          </Badge>
          <span className="text-xs text-muted-foreground">
            Margin: <span className={b.margin < 0 ? 'text-error font-bold' : b.margin < 5 ? 'text-warning font-bold' : 'text-success font-bold'}>{b.margin}%</span>
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2 text-xs font-mono">
        <div><span className="text-muted-foreground">Sales:</span> <span className="text-success">{cur()} {fmt(b.sales)}</span></div>
        <div><span className="text-muted-foreground">Expenses:</span> <span className="text-error">{cur()} {fmt(b.expenses)}</span></div>
        <div><span className="text-muted-foreground">Supplier:</span> <span className="text-warning">{cur()} {fmt(b.supplier)}</span></div>
        <div><span className="text-muted-foreground">Net:</span> <span className={b.net >= 0 ? 'text-success font-bold' : 'text-error font-bold'}>{cur()} {fmt(b.net)}</span></div>
        <div className="col-span-2 sm:col-span-1"><span className="text-muted-foreground">Top cost:</span> {(b.top_expenses || []).map(e => e.pct ? `${e.cat} (${e.pct}%)` : e.cat).slice(0, 2).join(', ') || '—'}</div>
      </div>

      {/* iter534 — Per-branch "kind of expenses + where to control" tip */}
      {b.concern_text && (
        <div className="mt-2 text-[11px] italic text-amber-700 dark:text-amber-400 leading-relaxed" data-testid={`branch-concern-${b.branch_id || b.name}`}>
          💡 {b.concern_text}
        </div>
      )}

      {open && (
        <div className="mt-3 pl-6 border-l-2 border-violet-200" data-testid={`branch-details-${b.branch_id || b.name}`}>
          {busy && <div className="text-xs text-muted-foreground flex items-center gap-2 py-2"><Loader2 size={12} className="animate-spin" /> Loading expense details…</div>}
          {!busy && details && (
            <>
              {details.count === 0 ? (
                <div className="text-xs text-muted-foreground italic py-2">No expense rows in this period.</div>
              ) : (
                <>
                  <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                    By category — {details.count} expense row{details.count === 1 ? '' : 's'} · total {cur()} {fmt(details.total)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mb-3">
                    {details.by_category.map((c) => (
                      <div key={c.category} className="flex justify-between px-2 py-1 rounded bg-muted/50 text-xs">
                        <span className="text-muted-foreground truncate mr-2">{c.category}</span>
                        <span className="font-mono font-semibold">{cur()} {fmt(c.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-violet-600 hover:underline">Show all {details.count} rows</summary>
                    <div className="mt-2 max-h-80 overflow-auto rounded border border-border">
                      <table className="w-full text-[11px]">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr className="text-left">
                            <th className="px-2 py-1 font-medium">Date</th>
                            <th className="px-2 py-1 font-medium">Category</th>
                            <th className="px-2 py-1 font-medium">Description</th>
                            <th className="px-2 py-1 font-medium text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details.expenses.map((e, i) => (
                            <tr key={e.id || i} className="border-t border-border">
                              <td className="px-2 py-1 font-mono whitespace-nowrap">{e.date}</td>
                              <td className="px-2 py-1">{e.category}</td>
                              <td className="px-2 py-1 truncate max-w-xs" title={e.description}>{e.description || '—'}</td>
                              <td className="px-2 py-1 font-mono text-right text-error">{cur()} {fmt(e.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function BranchLossAIPage() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showHealthy, setShowHealthy] = useState(true);
  // iter723 — Bottom sections: inter-branch balances + top suppliers.
  const [outstanding, setOutstanding] = useState(null);
  const [topSuppliers, setTopSuppliers] = useState(null);
  const [supplierSortBy, setSupplierSortBy] = useState('paid'); // 'paid' | 'outstanding' | 'purchased' | 'payment_count'
  const [supplierLimit, setSupplierLimit] = useState(10);
  const d = new Date();
  const first = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  const last = toLocalYmd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
  const [start, setStart] = useState(first);
  const [end, setEnd] = useState(last);

  const runAnalysis = async () => {
    setLoading(true);
    setData(null);
    setOutstanding(null);
    setTopSuppliers(null);
    try {
      const res = await api.get('/ai-insights/branch-loss', { params: { start_date: start, end_date: end } });
      setData(res.data);
      toast.success('Analysis ready');
    } catch (err) {
      toast.error('Failed to run analysis');
    } finally {
      setLoading(false);
    }
    // iter723 — Side-by-side fetch the two complementary lenses the
    // user explicitly asked for at the bottom of this page:
    //   1. Inter-branch balances owed (who owes whom right now)
    //   2. Top suppliers paid in this period (where the money went)
    // Both are best-effort — a failure here must NOT block the main
    // AI analysis. The cards just show empty states.
    try {
      const r = await api.get('/reports/inter-branch/outstanding');
      setOutstanding(r.data);
    } catch { /* silent — card renders empty state */ }
    try {
      const r = await api.get('/accounting/supplier-spent-report', {
        params: { start_date: start, end_date: end },
      });
      setTopSuppliers(r.data);
    } catch { /* silent — card renders empty state */ }
  };

  // iter520 — Export ALL branches (loss + healthy) to CSV.
  const exportCSV = () => {
    if (!data) return;
    const all = [
      ...(data.loss_branches || []).map(b => ({ ...b, status: b.is_loss ? 'LOSS' : 'WEAK MARGIN' })),
      ...(data.healthy_branches || []).map(b => ({ ...b, status: 'HEALTHY' })),
    ];
    if (all.length === 0) { toast.warning('Nothing to export'); return; }
    const header = ['Branch', 'Status', 'Sales (SR)', 'Expenses (SR)', 'Supplier (SR)', 'Net (SR)', 'Margin %', 'Top expense categories'];
    const rows = all.map(b => [
      `"${(b.name || '').replace(/"/g, '""')}"`,
      b.status,
      Number(b.sales || 0).toFixed(2),
      Number(b.expenses || 0).toFixed(2),
      Number(b.supplier || 0).toFixed(2),
      Number(b.net || 0).toFixed(2),
      Number(b.margin || 0).toFixed(1),
      `"${(b.top_expenses || []).map(e => `${e.cat} (${Number(e.amount || 0).toFixed(2)})`).join(' | ')}"`,
    ].join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `branch-loss-${data.period.start}_to_${data.period.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${all.length} branches`);
  };

  const allLoss = data?.loss_branches || [];
  const allHealthy = data?.healthy_branches || [];

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 sm:p-6 max-w-5xl mx-auto" data-testid="branch-loss-ai-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit flex items-center gap-2">
              <Brain className="text-violet-600" size={26} /> AI Branch-Loss Analysis
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Identify under-performing branches and get AI-generated mitigation actions by tier.</p>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div><Label className="text-[11px] text-muted-foreground">From</Label><Input type="date" min="2018-01-01" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-[150px]" /></div>
            <div><Label className="text-[11px] text-muted-foreground">To</Label><Input type="date" min="2018-01-01" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-[150px]" /></div>
            <Button onClick={runAnalysis} disabled={loading} className="h-9 bg-violet-600 hover:bg-violet-700" data-testid="run-branch-loss-btn">
              {loading ? <><Loader2 size={14} className="mr-2 animate-spin" />Analysing…</> : <><Brain size={14} className="mr-2" />Run Analysis</>}
            </Button>
            {data && (
              <Button onClick={exportCSV} variant="outline" className="h-9" data-testid="branch-loss-export-csv-btn">
                <Download size={14} className="mr-2" /> Export CSV
              </Button>
            )}
          </div>
        </div>

        {loading && (
          <Card><CardContent className="py-16 text-center text-muted-foreground space-y-3">
            <Loader2 className="mx-auto animate-spin text-violet-500" size={36} />
            <div>AI is analysing each branch&rsquo;s P&amp;L breakdown…</div>
          </CardContent></Card>
        )}

        {data && !loading && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground flex items-center gap-1"><TrendingDown size={12} className="text-error" />Under-performing</div><div className="text-2xl font-bold font-outfit mt-1 text-error">{allLoss.length}</div></CardContent></Card>
              <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 size={12} className="text-success" />Healthy</div><div className="text-2xl font-bold font-outfit mt-1 text-success">{allHealthy.length}</div></CardContent></Card>
              <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Period</div><div className="text-sm font-medium mt-2 font-mono">{data.period.start}<br />→ {data.period.end}</div></CardContent></Card>
            </div>

            {allLoss.length > 0 && (
              <Card data-testid="loss-branches-card"><CardContent className="p-0">
                <div className="px-4 py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={16} /><span className="font-semibold">Branches Losing Money or Below 5% Margin ({allLoss.length})</span>
                  </div>
                </div>
                <div className="divide-y divide-border">
                  {allLoss.map((b) => (
                    <BranchRow
                      key={b.branch_id || b.name}
                      b={b}
                      start={start}
                      end={end}
                      kind={b.is_loss ? 'loss' : 'weak'}
                    />
                  ))}
                </div>
              </CardContent></Card>
            )}

            {/* iter520 — Show ALL healthy branches with the same card pattern */}
            {allHealthy.length > 0 && (
              <Card data-testid="healthy-branches-card"><CardContent className="p-0">
                <button
                  type="button"
                  onClick={() => setShowHealthy(s => !s)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white flex items-center justify-between hover:from-emerald-600 hover:to-teal-600 transition-colors"
                  data-testid="toggle-healthy-branches"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={16} /><span className="font-semibold">Healthy Branches ({allHealthy.length})</span>
                  </div>
                  {showHealthy ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {showHealthy && (
                  <div className="divide-y divide-border">
                    {allHealthy.map((b) => (
                      <BranchRow
                        key={b.branch_id || b.name}
                        b={b}
                        start={start}
                        end={end}
                        kind="healthy"
                      />
                    ))}
                  </div>
                )}
              </CardContent></Card>
            )}

            {/* AI analysis */}
            <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-white">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="text-violet-600" size={18} />
                  <span className="font-semibold font-outfit">AI Recommendations</span>
                </div>
                <pre className="text-sm whitespace-pre-wrap leading-relaxed text-foreground">{data.analysis}</pre>
                <div className="text-[10px] text-muted-foreground mt-4 italic">Generated at {new Date(data.generated_at).toLocaleString()} · Powered by OpenAI GPT-4.1</div>
              </CardContent>
            </Card>

            {/* iter723 — Inter-branch balances owed (who owes whom right now).
                Calls /api/reports/inter-branch/outstanding which is the
                canonical tenant-wide "outstanding pair" report. Renders
                even if empty so users see "all settled". */}
            <Card data-testid="inter-branch-outstanding-card">
              <CardContent className="p-0">
                <div className="px-4 py-3 bg-gradient-to-r from-sky-600 to-indigo-600 text-white">
                  <div className="flex items-center gap-2">
                    <ArrowRightLeft size={16} />
                    <span className="font-semibold">
                      Inter-Branch Balances Owed
                      {outstanding && (
                        <span className="ml-2 text-[11px] opacity-80">
                          ({outstanding.outstanding?.length || 0} open pair{(outstanding.outstanding?.length || 0) === 1 ? '' : 's'} · as of {outstanding.as_of})
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                {!outstanding ? (
                  <div className="p-6 text-center text-muted-foreground text-sm" data-testid="inter-branch-loading">
                    <Loader2 className="mx-auto mb-2 animate-spin" size={20} />
                    Loading inter-branch balances…
                  </div>
                ) : (outstanding.outstanding || []).length === 0 ? (
                  <div className="p-6 text-center text-success text-sm" data-testid="inter-branch-empty">
                    <CheckCircle2 className="mx-auto mb-2" size={20} />
                    All branches are square — no open inter-branch balances.
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {(outstanding.outstanding || [])
                      .filter(o => Number(o.owes || 0) > 0.005)
                      .sort((a, b) => Number(b.owes || 0) - Number(a.owes || 0))
                      .map((o, idx) => (
                      <div
                        key={`${o.from_id}-${o.to_id}-${idx}`}
                        className="px-4 py-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center hover:bg-muted/30"
                        data-testid={`inter-branch-row-${idx}`}
                      >
                        <div className="text-sm">
                          <span className="font-semibold text-error">{o.from_branch}</span>
                          <ArrowRightLeft size={12} className="inline mx-2 text-muted-foreground" />
                          <span className="font-semibold text-success">{o.to_branch}</span>
                          <Badge variant="outline" className="ml-2 text-[10px] bg-muted/50">
                            {o.transaction_count} txn{o.transaction_count === 1 ? '' : 's'}
                          </Badge>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {o.from_branch} owes {o.to_branch} after settlements
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold font-outfit text-error">{cur(o.owes)}</div>
                          <div className="text-[10px] text-muted-foreground">
                            paid for: {cur(o.forward_total)} · settled: {cur(o.settled_forward)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {outstanding && outstanding.branch_summary?.length > 0 && (
                  <div className="px-4 py-3 bg-muted/20 border-t border-border" data-testid="inter-branch-summary">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 font-semibold">Per-branch net</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {outstanding.branch_summary.map((s) => (
                        <div key={s.branch_id} className="bg-white border border-border rounded p-2">
                          <div className="text-[10px] text-muted-foreground truncate">{s.branch_name}</div>
                          <div className={`text-sm font-bold ${Number(s.net || 0) > 0 ? 'text-success' : Number(s.net || 0) < 0 ? 'text-error' : 'text-muted-foreground'}`}>
                            {cur(s.net)}
                          </div>
                          <div className="text-[9px] text-muted-foreground">
                            paid out: {cur(s.paid_for_others)} · in: {cur(s.paid_by_others)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* iter723 — Top suppliers paid in this period. Drop-list users
                requested so they can spot "who did we pay the most this
                month" at a glance. Sortable by paid / outstanding /
                purchased / payment_count. */}
            <Card data-testid="top-suppliers-card">
              <CardContent className="p-0">
                <div className="px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Users size={16} />
                    <span className="font-semibold">
                      Top Suppliers — Paid in Period
                      {topSuppliers?.suppliers && (
                        <span className="ml-2 text-[11px] opacity-80">({topSuppliers.suppliers.length} total)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px]">
                    <select
                      value={supplierSortBy}
                      onChange={(e) => setSupplierSortBy(e.target.value)}
                      className="bg-white/15 border border-white/30 rounded px-1.5 py-0.5 text-white"
                      data-testid="top-suppliers-sort"
                    >
                      <option className="text-foreground" value="paid">Sort: Paid (highest)</option>
                      <option className="text-foreground" value="outstanding">Sort: Outstanding</option>
                      <option className="text-foreground" value="purchased">Sort: Purchased</option>
                      <option className="text-foreground" value="payment_count">Sort: # Payments</option>
                    </select>
                    <select
                      value={supplierLimit}
                      onChange={(e) => setSupplierLimit(Number(e.target.value))}
                      className="bg-white/15 border border-white/30 rounded px-1.5 py-0.5 text-white"
                      data-testid="top-suppliers-limit"
                    >
                      <option className="text-foreground" value={5}>Top 5</option>
                      <option className="text-foreground" value={10}>Top 10</option>
                      <option className="text-foreground" value={20}>Top 20</option>
                      <option className="text-foreground" value={9999}>All</option>
                    </select>
                  </div>
                </div>
                {!topSuppliers ? (
                  <div className="p-6 text-center text-muted-foreground text-sm" data-testid="top-suppliers-loading">
                    <Loader2 className="mx-auto mb-2 animate-spin" size={20} />
                    Loading top suppliers…
                  </div>
                ) : !(topSuppliers.suppliers || []).length ? (
                  <div className="p-6 text-center text-muted-foreground text-sm" data-testid="top-suppliers-empty">
                    No supplier activity in this period.
                  </div>
                ) : (
                  <>
                    <div className="hidden sm:grid sm:grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-4 py-2 bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                      <span><ArrowUpDown size={10} className="inline mr-1" />Supplier</span>
                      <span className="text-right">Paid</span>
                      <span className="text-right">Purchased</span>
                      <span className="text-right">Outstanding</span>
                      <span className="text-right"># Pays</span>
                      <span className="text-right">% of total</span>
                    </div>
                    <div className="divide-y divide-border">
                      {[...(topSuppliers.suppliers || [])]
                        .sort((a, b) => Number(b[supplierSortBy] || 0) - Number(a[supplierSortBy] || 0))
                        .slice(0, supplierLimit)
                        .map((s, idx) => (
                          <div
                            key={s.supplier_id || idx}
                            className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-4 py-2.5 text-xs hover:bg-muted/20"
                            data-testid={`top-supplier-row-${idx}`}
                          >
                            <div>
                              <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold mr-2">{idx + 1}</span>
                              <span className="font-medium">{s.supplier_name || 'Unknown'}</span>
                              {s.category && <Badge variant="outline" className="ml-2 text-[9px] bg-muted/50">{s.category}</Badge>}
                            </div>
                            <div className="text-right font-semibold text-info" data-testid={`top-supplier-paid-${idx}`}>{cur(s.paid)}</div>
                            <div className="text-right text-muted-foreground">{cur(s.purchased)}</div>
                            <div className={`text-right font-semibold ${Number(s.outstanding || 0) > 0 ? 'text-warning' : 'text-muted-foreground'}`}>
                              {cur(s.outstanding)}
                            </div>
                            <div className="text-right text-muted-foreground">{s.payment_count || 0}</div>
                            <div className="text-right text-muted-foreground">{Number(s.pct_of_total || 0).toFixed(1)}%</div>
                          </div>
                        ))}
                    </div>
                    <div className="px-4 py-2 bg-muted/30 border-t border-border text-[10px] text-muted-foreground flex items-center justify-between flex-wrap gap-1">
                      <span>Grand total paid: <strong className="text-info">{cur(topSuppliers.grand_total)}</strong> across {topSuppliers.supplier_count} supplier{topSuppliers.supplier_count === 1 ? '' : 's'}</span>
                      <span>Sorted by <strong>{supplierSortBy}</strong> · Period {data?.period?.start} → {data?.period?.end}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {!data && !loading && (
          <Card><CardContent className="py-16 text-center text-muted-foreground">
            <Brain className="mx-auto mb-3 text-violet-400" size={40} />
            <div>Click <strong>Run Analysis</strong> to get AI-powered branch-loss insights for the chosen period.</div>
          </CardContent></Card>
        )}
      </div>
    </DashboardLayout>
  );
}
